import { z } from "zod";
import { typeDate } from "./date.js";
import { formatPhone } from "./phone.js";
import { PARTNER_CODE_RE, partnerCodeOf } from "./partnerSearch.js";

/**
 * データの取込（#268 1.5c-1、docs/98 §6・docs/68 の土台を請求書で先に作った）。
 *   1回目は「未入金の請求書」だけ：前のシステム（Board など）で出した、まだ入金の無い請求書を CSV で取り込み、発行済（取込）の請求書として入金待ちに出す。
 *   画面の流れ＝取込元とファイル → 読み込みの確認（先頭 5 行・文字コード）→ 列の対応 → 確認（試し取込＝まだ登録しない）→ 取込 → 履歴（14 日は取消できる）
 *   CSV の読み方・列の対応・行の確かめはここ1か所（API が使う。画面は API の答えを見せるだけ）
 */

export const IMPORT_LIMITS = {
  /** ファイルの大きさ（docs/68 §5） */
  bytes: 10 * 1024 * 1024,
  /** 行（見出しを除く） */
  rows: 5000,
  /** 1行の列 */
  cols: 200,
  /** 読み込みの確認に見せる行 */
  previewRows: 5,
  /** 取消できる日数（docs/68 §4） */
  undoDays: 14,
  /** 元のファイルを消すまでの日数（docs/68 §5） */
  fileDays: 7,
  /** 確認の画面の1ページの行 */
  pageRows: 200,
} as const;

export const ImportKind = z.enum(["unpaid_invoices", "partners"]);
export type ImportKind = z.infer<typeof ImportKind>;
export const ImportSource = z.enum(["board", "generic"]);
export type ImportSource = z.infer<typeof ImportSource>;
export const ImportEncoding = z.enum(["utf8", "cp932"]);
export type ImportEncoding = z.infer<typeof ImportEncoding>;
export const ImportStatus = z.enum(["checking", "ready", "imported", "undone", "expired"]);
export type ImportStatus = z.infer<typeof ImportStatus>;

export const IMPORT_SOURCE_LABEL: Record<ImportSource, string> = { board: "Board", generic: "その他（CSV）" };
export const IMPORT_KIND_LABEL: Record<ImportKind, string> = { unpaid_invoices: "請求書（未入金）", partners: "取引先" };
export const IMPORT_STATUS_LABEL: Record<ImportStatus, string> = { checking: "確認中", ready: "確認済", imported: "取込済", undone: "取消済", expired: "取込済" };
export const IMPORT_ENCODING_LABEL: Record<ImportEncoding, string> = { utf8: "UTF-8", cp932: "Shift_JIS（CP932）" };

/** 未入金の請求書の項目（列の対応の右側）。label は言葉のキーを含む（画面は t()） */
export const UNPAID_FIELDS = [
  { key: "managementNo", label: "請求書番号", required: true },
  { key: "clientName", label: "{customer}", required: true },
  { key: "subject", label: "{subject}", required: false },
  { key: "subtotal", label: "税抜金額", required: false },
  { key: "tax", label: "消費税", required: false },
  { key: "total", label: "税込金額", required: true },
  { key: "issueDate", label: "請求日", required: true },
  { key: "dueDate", label: "お支払期限", required: true },
  { key: "notes", label: "備考", required: false },
] as const;
export type UnpaidField = (typeof UNPAID_FIELDS)[number]["key"];
export const UnpaidFieldKey = z.enum(UNPAID_FIELDS.map((f) => f.key) as [UnpaidField, ...UnpaidField[]]);

/** 列の対応：項目 → 列の位置（0 から。null＝取り込まない）。項目は取込の種類ごと（UNPAID_FIELDS・PARTNER_FIELDS） */
export const ImportMapping = z.record(z.string().max(40), z.number().int().min(0).max(IMPORT_LIMITS.cols - 1).nullable());
export type ImportMapping = z.infer<typeof ImportMapping>;

/**
 * 列の名前の候補（見出しを NFKC・空白なしで比べる）。Board の請求一覧（請求単位）の書き出しの列と、よくある名前。
 * 先に書いたものから当てる（同じ列を2つの項目に当てない）
 */
export const UNPAID_HEADER_GUESS: Record<UnpaidField, readonly string[]> = {
  managementNo: ["請求書番号", "請求番号", "請求書No", "請求書NO", "書類番号", "番号", "No", "NO"],
  clientName: ["顧客名", "請求先", "請求先名", "取引先", "取引先名", "得意先", "得意先名", "会社名", "宛名"],
  subject: ["件名", "請求書件名", "タイトル", "案件名", "工事名"],
  subtotal: ["小計", "税抜金額", "税抜", "金額(税抜)", "税抜合計", "小計金額"],
  tax: ["消費税", "消費税額", "税額", "消費税等"],
  total: ["合計", "合計金額", "請求金額", "税込金額", "税込", "金額(税込)", "税込合計", "ご請求金額"],
  issueDate: ["請求日", "発行日", "請求書日付", "日付"],
  dueDate: ["支払期限", "お支払期限", "支払期日", "入金予定日", "入金期日", "振込期限"],
  notes: ["備考", "メモ", "社内メモ"],
};

const normHeader = (s: string): string => s.normalize("NFKC").replace(/[\s　()（）]/g, "").toLowerCase();

/** 見出しから列の対応の候補（見つからない項目は null） */
export function guessUnpaidMapping(headers: readonly string[]): ImportMapping {
  const used = new Set<number>();
  const out = {} as Record<UnpaidField, number | null>;
  const hs = headers.map(normHeader);
  for (const f of UNPAID_FIELDS) {
    let hit: number | null = null;
    for (const cand of UNPAID_HEADER_GUESS[f.key]) {
      const c = normHeader(cand);
      const i = hs.findIndex((h, k) => !used.has(k) && h === c);
      if (i >= 0) {
        hit = i;
        break;
      }
    }
    if (hit != null) used.add(hit);
    out[f.key] = hit;
  }
  return out;
}

/** バイト列 → 文字（BOM → UTF-8 → CP932 の順。encoding を渡せばそれで読む） */
export function decodeCsv(bytes: Uint8Array, encoding?: ImportEncoding): { text: string; encoding: ImportEncoding } {
  const bom = bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
  if (encoding === "cp932") return { text: new TextDecoder("shift_jis").decode(bytes), encoding };
  if (encoding === "utf8" || bom) return { text: new TextDecoder("utf-8").decode(bom ? bytes.subarray(3) : bytes), encoding: "utf8" };
  try {
    return { text: new TextDecoder("utf-8", { fatal: true }).decode(bytes), encoding: "utf8" };
  } catch {
    return { text: new TextDecoder("shift_jis").decode(bytes), encoding: "cp932" };
  }
}

/** CSV／TSV（RFC 4180：引用符・引用符の中の改行と "" ）→ 行の配列。区切りは1行目でタブが多ければタブ。空の行は捨てる */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, "");
  const firstLine = src.split(/\r?\n/, 1)[0] ?? "";
  const sep = (firstLine.match(/\t/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let q = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (q) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else q = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && cell === "") q = true;
    else if (ch === sep) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      cell = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim() !== "")) rows.push(row);
  return rows;
}

/** CSV の金額（「¥1,100」「1,100円」「１１００」「▲100」「(100)」「-100」）→ 整数の円。空＝null・読めない＝NaN */
export function parseCsvYen(raw: string): number | null {
  let t = raw.normalize("NFKC").replace(/[\s,¥\\円]/g, "");
  if (!t) return null;
  let neg = false;
  if (/^[▲△-]/.test(t)) {
    neg = true;
    t = t.slice(1);
  } else if (/^\(.*\)$/.test(t)) {
    neg = true;
    t = t.slice(1, -1);
  }
  if (!/^\d+(\.0+)?$/.test(t)) return Number.NaN;
  const n = Number(t.replace(/\.0+$/, ""));
  return Number.isSafeInteger(n) ? (neg ? -n : n) : Number.NaN;
}

/** CSV の日付 → YYYY-MM-DD（空＝null・読めない＝""） */
export function parseCsvDate(raw: string): string | null {
  const t = typeDate(raw);
  if (t === "") return null;
  if (t == null) return "";
  return t.replace(/\//g, "-");
}

/** 取り込む値（1行） */
export const UnpaidRowValue = z.object({
  managementNo: z.string(),
  clientName: z.string(),
  subject: z.string(),
  subtotal: z.number().int().nullable(),
  tax: z.number().int().nullable(),
  total: z.number().int().nullable(),
  issueDate: z.string().nullable(),
  dueDate: z.string().nullable(),
  notes: z.string(),
});
export type UnpaidRowValue = z.infer<typeof UnpaidRowValue>;

/**
 * 指摘（コードと欄の名前だけ＝個人情報を入れない。docs/68 §5）
 *   error＝その行は取り込めない（直すか「取り込まない」）／confirm＝決めないと進めない（{customer}が無い＝登録か取り込まない）／warn＝取り込める
 */
export const IMPORT_ISSUE_CODES = [
  "required",
  "amount_bad",
  "amount_mismatch",
  "amount_nonpositive",
  "date_bad",
  "no_too_long",
  "duplicate_in_file",
  "duplicate_existing",
  "client_not_found",
  "due_before_issue",
  "formula",
  "amount_derived",
  // 取引先（#288）
  "code_bad",
  "type_bad",
  "phone_bad",
  "zip_bad",
  "corp_bad",
  "reg_bad",
  "name_conflict",
  "code_conflict",
  "branch_required",
] as const;
export const ImportIssueCode = z.enum(IMPORT_ISSUE_CODES);
export type ImportIssueCode = z.infer<typeof ImportIssueCode>;
export const ImportIssue = z.object({ code: ImportIssueCode, field: z.string().max(40).nullable() });
export type ImportIssue = z.infer<typeof ImportIssue>;
export type ImportSeverity = "error" | "confirm" | "warn";
export const IMPORT_ISSUE_SEVERITY: Record<ImportIssueCode, ImportSeverity> = {
  required: "error",
  amount_bad: "error",
  amount_mismatch: "error",
  amount_nonpositive: "error",
  date_bad: "error",
  no_too_long: "error",
  duplicate_in_file: "error",
  duplicate_existing: "error",
  client_not_found: "confirm",
  due_before_issue: "warn",
  formula: "warn",
  amount_derived: "warn",
  code_bad: "error",
  type_bad: "error",
  phone_bad: "error",
  zip_bad: "error",
  corp_bad: "error",
  reg_bad: "error",
  name_conflict: "error",
  code_conflict: "error",
  branch_required: "error",
};

/** 1行の値を作る（列の対応のとおりに取り出して直す）。金額は2つあれば残りを足し引きで（amount_derived） */
export function unpaidValueOf(cells: readonly string[], mapping: ImportMapping): { value: UnpaidRowValue; issues: ImportIssue[] } {
  const get = (k: UnpaidField): string => {
    const i = mapping[k];
    return i == null ? "" : (cells[i] ?? "").trim();
  };
  const issues: ImportIssue[] = [];
  const money = (k: "subtotal" | "tax" | "total"): number | null => {
    const n = parseCsvYen(get(k));
    if (n != null && Number.isNaN(n)) {
      issues.push({ code: "amount_bad", field: k });
      return null;
    }
    return n;
  };
  const date = (k: "issueDate" | "dueDate"): string | null => {
    const d = parseCsvDate(get(k));
    if (d === "") {
      issues.push({ code: "date_bad", field: k });
      return null;
    }
    return d;
  };
  let subtotal = money("subtotal");
  let tax = money("tax");
  let total = money("total");
  const badMoney = issues.some((x) => x.code === "amount_bad");
  if (!badMoney) {
    if (total != null && subtotal != null && tax == null) {
      tax = total - subtotal;
      issues.push({ code: "amount_derived", field: "tax" });
    } else if (total != null && tax != null && subtotal == null) {
      subtotal = total - tax;
      issues.push({ code: "amount_derived", field: "subtotal" });
    } else if (total == null && subtotal != null && tax != null) {
      total = subtotal + tax;
      issues.push({ code: "amount_derived", field: "total" });
    }
  }
  const value: UnpaidRowValue = {
    managementNo: get("managementNo").normalize("NFKC").trim(),
    clientName: get("clientName").replace(/[\s　]+/g, " ").trim(),
    subject: get("subject").slice(0, 200),
    subtotal,
    tax,
    total,
    issueDate: date("issueDate"),
    dueDate: date("dueDate"),
    notes: get("notes").slice(0, 2000),
  };
  return { value, issues };
}

/** 1行の確かめ（ほかの行・今ある番号・{customer}は ctx から） */
export function checkUnpaidRow(
  value: UnpaidRowValue,
  base: ImportIssue[],
  ctx: { duplicateInFile: boolean; existingNo: boolean; clientFound: boolean },
): ImportIssue[] {
  const issues: ImportIssue[] = [...base];
  const has = (code: ImportIssueCode, field: UnpaidField | null) => issues.some((x) => x.code === code && x.field === field);
  if (!value.managementNo) issues.push({ code: "required", field: "managementNo" });
  else if (value.managementNo.length > 50) issues.push({ code: "no_too_long", field: "managementNo" });
  if (!value.clientName) issues.push({ code: "required", field: "clientName" });
  if (value.total == null && !has("amount_bad", "total")) issues.push({ code: "required", field: "total" });
  if (value.subtotal == null && value.tax == null && value.total != null) issues.push({ code: "required", field: "tax" });
  if (value.issueDate == null && !has("date_bad", "issueDate")) issues.push({ code: "required", field: "issueDate" });
  if (value.dueDate == null && !has("date_bad", "dueDate")) issues.push({ code: "required", field: "dueDate" });
  if (value.subtotal != null && value.tax != null && value.total != null) {
    if (value.subtotal + value.tax !== value.total) issues.push({ code: "amount_mismatch", field: "total" });
    else if (value.total <= 0 || value.tax < 0) issues.push({ code: "amount_nonpositive", field: "total" });
  }
  if (value.issueDate && value.dueDate && value.dueDate < value.issueDate) issues.push({ code: "due_before_issue", field: "dueDate" });
  if (value.managementNo && ctx.duplicateInFile) issues.push({ code: "duplicate_in_file", field: "managementNo" });
  if (value.managementNo && ctx.existingNo) issues.push({ code: "duplicate_existing", field: "managementNo" });
  if (value.clientName && !ctx.clientFound) issues.push({ code: "client_not_found", field: "clientName" });
  for (const k of ["managementNo", "clientName", "subject", "notes"] as const) {
    if (/^[=+\-@＝＋－＠\t\r]/.test(value[k])) issues.push({ code: "formula", field: k });
  }
  return issues;
}

/** 行の状態（確認の画面の絞り込み）：取り込まない／エラー／要確認／取り込む */
export type ImportRowState = "skip" | "error" | "confirm" | "ok";
export function importRowState(issues: readonly ImportIssue[], action: "new" | "skip", createClient: boolean): ImportRowState {
  if (action === "skip") return "skip";
  if (issues.some((x) => IMPORT_ISSUE_SEVERITY[x.code] === "error")) return "error";
  if (!createClient && issues.some((x) => x.code === "client_not_found")) return "confirm";
  return "ok";
}

/** {customer}の名前を揃える（前後の空白・続く空白は1つ。DB の fn_import_client と同じ） */
export const importClientKey = (name: string): string => name.replace(/\s+/g, " ").trim();


// ---------------------------------------------------------------------------
// 取引先の取込（#288、docs/101 §3・docs/68 の2回目の一部）
//   キー＝顧客コード → 仕入先コード → 名称（空白をそろえて一致）。当たれば更新（空の欄は今の値のまま）、無ければ登録。
//   同じ取引先を何行かに分けて支店を足せる（2行目からは支店名が要る）。仕入先コードに接頭辞（例 S-）を付けられる
// ---------------------------------------------------------------------------
export const PARTNER_FIELDS = [
  { key: "name", label: "名称", required: true },
  { key: "shortName", label: "略称", required: false },
  { key: "nameKana", label: "フリガナ", required: false },
  { key: "partnerType", label: "区分", required: false },
  { key: "customerCode", label: "顧客コード", required: false },
  { key: "vendorCode", label: "仕入先コード", required: false },
  { key: "phone", label: "電話", required: false },
  { key: "fax", label: "FAX", required: false },
  { key: "postalCode", label: "郵便番号", required: false },
  { key: "address", label: "住所", required: false },
  { key: "corporateNumber", label: "法人番号", required: false },
  { key: "invoiceRegistrationNo", label: "登録番号", required: false },
  { key: "branchName", label: "支店名", required: false },
  { key: "branchCode", label: "支店コード", required: false },
] as const;
export type PartnerField = (typeof PARTNER_FIELDS)[number]["key"];

/** 列の名前の候補（Board の顧客 CSV・持ち出しの CSV（一覧/{customer}.csv）・よくある名前） */
export const PARTNER_HEADER_GUESS: Record<PartnerField, readonly string[]> = {
  name: ["名称", "名前", "顧客名", "取引先名", "得意先名", "仕入先名", "会社名", "正式名称"],
  shortName: ["略称", "顧客略称名", "取引先略称", "略称名"],
  nameKana: ["フリガナ", "ふりがな", "カナ", "顧客名カナ", "取引先名カナ", "名称カナ"],
  partnerType: ["区分", "取引先区分", "種別"],
  customerCode: ["顧客コード", "得意先コード", "顧客番号", "取引先コード", "コード"],
  vendorCode: ["仕入先コード", "外注先コード", "仕入先番号"],
  phone: ["電話", "電話番号", "TEL", "代表電話"],
  fax: ["FAX", "FAX番号", "ファックス"],
  postalCode: ["郵便番号", "〒"],
  address: ["住所", "所在地", "住所1"],
  corporateNumber: ["法人番号"],
  invoiceRegistrationNo: ["登録番号", "適格請求書発行事業者登録番号", "インボイス登録番号"],
  branchName: ["支店名", "支店・営業所", "営業所名", "支店"],
  branchCode: ["支店コード", "営業所コード"],
};

/** 見出しから列の対応の候補（取引先） */
export function guessPartnerMapping(headers: readonly string[]): ImportMapping {
  const used = new Set<number>();
  const out: Record<string, number | null> = {};
  const hs = headers.map(normHeader);
  for (const f of PARTNER_FIELDS) {
    let hit: number | null = null;
    for (const cand of PARTNER_HEADER_GUESS[f.key]) {
      const c = normHeader(cand);
      const i = hs.findIndex((h, k) => !used.has(k) && h === c);
      if (i >= 0) {
        hit = i;
        break;
      }
    }
    if (hit != null) used.add(hit);
    out[f.key] = hit;
  }
  return out;
}

/** 取込の選択肢（imports.options） */
export const ImportOptions = z.object({
  /** 仕入先コードに付ける接頭辞（例 S-。他社の台帳で得意先と仕入先が同じコードのとき）。空＝付けない */
  vendorCodePrefix: z.string().transform((s) => s.normalize("NFKC").trim()).pipe(z.string().regex(/^[A-Za-z0-9_-]{0,5}$/, "接頭辞は半角英数字とハイフン・アンダーバーで5文字までです")).default(""),
});
export type ImportOptions = z.infer<typeof ImportOptions>;

/** 取り込む取引先（1行） */
export const PartnerRowValue = z.object({
  name: z.string(),
  shortName: z.string(),
  nameKana: z.string(),
  /** 区分の列か、コードから決めた種類。explicit＝区分の列かコードがあった（更新で種類を広げる） */
  partnerType: z.enum(["client", "vendor", "both"]),
  typeExplicit: z.boolean(),
  customerCode: z.string(),
  vendorCode: z.string(),
  phone: z.string(),
  fax: z.string(),
  postalCode: z.string(),
  address: z.string(),
  corporateNumber: z.string(),
  invoiceRegistrationNo: z.string(),
  branchName: z.string(),
  branchCode: z.string(),
});
export type PartnerRowValue = z.infer<typeof PartnerRowValue>;

/** 確かめで当たった今の取引先（更新）。無ければ登録 */
export const PartnerRowMatch = z.object({ partnerId: z.string().uuid(), name: z.string(), by: z.enum(["customerCode", "vendorCode", "name"]) });
export type PartnerRowMatch = z.infer<typeof PartnerRowMatch>;

const TYPE_WORDS: Array<[RegExp, "client" | "vendor" | "both"]> = [
  [/^(両方|both|顧客・仕入先|得意先・仕入先|顧客\/仕入先)$/i, "both"],
  [/^(顧客|得意先|client|customer|販売先)$/i, "client"],
  [/^(仕入先|外注先|vendor|supplier|発注先)$/i, "vendor"],
];

/** 取引先の名前を揃える（前後の空白・続く空白は1つ。importClientKey と同じ） */
export const partnerNameKey = (name: string): string => name.normalize("NFKC").replace(/\s+/g, " ").trim();

/** 1行の値を作る（列の対応のとおりに取り出して直す）＋形の確かめ */
export function partnerValueOf(cells: readonly string[], mapping: ImportMapping, options: ImportOptions): { value: PartnerRowValue; issues: ImportIssue[] } {
  const get = (k: PartnerField): string => {
    const i = mapping[k];
    return i == null ? "" : (cells[i] ?? "").trim();
  };
  const issues: ImportIssue[] = [];
  const code = (k: "customerCode" | "vendorCode" | "branchCode", prefix = ""): string => {
    const c = partnerCodeOf(get(k));
    if (!c) return "";
    const v = prefix + c;
    if (!PARTNER_CODE_RE.test(v)) issues.push({ code: "code_bad", field: k });
    return v;
  };
  const customerCode = code("customerCode");
  const vendorCode = code("vendorCode", options.vendorCodePrefix);
  const typeRaw = get("partnerType").normalize("NFKC").trim();
  let partnerType: "client" | "vendor" | "both" = customerCode && vendorCode ? "both" : vendorCode ? "vendor" : "client";
  let typeExplicit = !!(customerCode || vendorCode);
  if (typeRaw) {
    const hit = TYPE_WORDS.find(([re]) => re.test(typeRaw));
    if (hit) {
      partnerType = hit[1];
      typeExplicit = true;
    } else issues.push({ code: "type_bad", field: "partnerType" });
  }
  const phone = (k: "phone" | "fax"): string => {
    const raw = get(k);
    if (!raw) return "";
    if (!/^[0-9０-９\-－()（）+ ]{1,20}$/.test(raw)) {
      issues.push({ code: "phone_bad", field: k });
      return raw;
    }
    return formatPhone(raw);
  };
  const zipRaw = get("postalCode").normalize("NFKC").replace(/[〒\s-]/g, "");
  if (zipRaw && !/^\d{7}$/.test(zipRaw)) issues.push({ code: "zip_bad", field: "postalCode" });
  const corp = get("corporateNumber").normalize("NFKC").replace(/[\s-]/g, "");
  if (corp && !/^\d{13}$/.test(corp)) issues.push({ code: "corp_bad", field: "corporateNumber" });
  const reg = get("invoiceRegistrationNo").normalize("NFKC").replace(/[\s-]/g, "").toUpperCase();
  if (reg && !/^T\d{13}$/.test(reg)) issues.push({ code: "reg_bad", field: "invoiceRegistrationNo" });
  const value: PartnerRowValue = {
    name: get("name").replace(/\s+/g, " ").trim().slice(0, 200),
    shortName: get("shortName").slice(0, 50),
    nameKana: get("nameKana").slice(0, 100),
    partnerType,
    typeExplicit,
    customerCode,
    vendorCode,
    phone: phone("phone"),
    fax: phone("fax"),
    postalCode: zipRaw,
    address: get("address").slice(0, 200),
    corporateNumber: corp,
    invoiceRegistrationNo: reg,
    branchName: get("branchName").slice(0, 100),
    branchCode: code("branchCode"),
  };
  if (!value.name) issues.push({ code: "required", field: "name" });
  if (value.branchCode && !value.branchName) issues.push({ code: "branch_required", field: "branchName" });
  for (const k of ["name", "shortName", "nameKana", "address", "branchName"] as const) {
    if (/^[=+\-@＝＋－＠\t\r]/.test(value[k])) issues.push({ code: "formula", field: k });
  }
  return { value, issues };
}

/** 取引先の行のキー（同じ取引先の行をまとめる）：顧客コード → 仕入先コード → 名称 */
export const partnerRowKey = (v: PartnerRowValue): string =>
  v.customerCode ? `c:${v.customerCode.toUpperCase()}` : v.vendorCode ? `v:${v.vendorCode.toUpperCase()}` : `n:${partnerNameKey(v.name)}`;

// ---------------------------------------------------------------------------
// API の形
// ---------------------------------------------------------------------------
export const ImportPreview = z.object({
  id: z.string().uuid(),
  kind: ImportKind,
  source: ImportSource,
  fileName: z.string(),
  encoding: ImportEncoding,
  headers: z.array(z.string()),
  sample: z.array(z.array(z.string())),
  rowCount: z.number().int(),
  mapping: ImportMapping,
  options: ImportOptions.default({ vendorCodePrefix: "" }),
});
export type ImportPreview = z.infer<typeof ImportPreview>;

export const ImportRowView = z.object({
  id: z.string().uuid(),
  rowNo: z.number().int(),
  /** 請求書（unpaid_invoices）は UnpaidRowValue、取引先（partners）は PartnerRowValue（画面は ImportDetail.kind で見分ける） */
  value: z.union([UnpaidRowValue, PartnerRowValue]),
  issues: z.array(ImportIssue),
  action: z.enum(["new", "skip"]),
  createClient: z.boolean(),
  state: z.enum(["skip", "error", "confirm", "ok"]),
  documentId: z.string().uuid().nullable(),
  /** 取引先：当たった今の取引先（更新）。null＝登録。同じ取引先の2行目からは first に1行目の番号 */
  match: PartnerRowMatch.nullable().default(null),
  first: z.number().int().nullable().default(null),
  /** 取り込んだあと：登録・更新した取引先 */
  partnerId: z.string().uuid().nullable().default(null),
});
export type ImportRowView = z.infer<typeof ImportRowView>;

export const ImportCounts = z.object({
  total: z.number().int(),
  ok: z.number().int(),
  confirm: z.number().int(),
  error: z.number().int(),
  skip: z.number().int(),
  /** 取り込むと登録する{customer}の数 */
  newClients: z.number().int(),
  /** 取り込む請求書の税込の合計 */
  amount: z.number().int(),
  /** 取引先：登録する数・更新する数（#288） */
  newPartners: z.number().int().default(0),
  updatePartners: z.number().int().default(0),
});
export type ImportCounts = z.infer<typeof ImportCounts>;

export const ImportDetail = z.object({
  id: z.string().uuid(),
  kind: ImportKind,
  source: ImportSource,
  fileName: z.string(),
  encoding: ImportEncoding,
  status: ImportStatus,
  mapping: ImportMapping,
  options: ImportOptions.default({ vendorCodePrefix: "" }),
  counts: ImportCounts,
  rows: z.array(ImportRowView),
  createdAt: z.string(),
  importedAt: z.string().nullable(),
  undoUntil: z.string().nullable(),
  undoneAt: z.string().nullable(),
  /** 取り込んだ・残した数（取込のあと） */
  result: z
    .object({
      imported: z.number().int(),
      clients: z.number().int(),
      undoDeleted: z.number().int().nullable(),
      undoKept: z.number().int().nullable(),
      /** 取引先：登録・更新した数（#288） */
      created: z.number().int().default(0),
      updated: z.number().int().default(0),
    })
    .nullable(),
});
export type ImportDetail = z.infer<typeof ImportDetail>;

export const ImportSummary = z.object({
  id: z.string().uuid(),
  kind: ImportKind,
  source: ImportSource,
  fileName: z.string(),
  status: ImportStatus,
  createdAt: z.string(),
  importedAt: z.string().nullable(),
  undoUntil: z.string().nullable(),
  /** 取消できるか（取込済・期限の中） */
  undoable: z.boolean(),
  imported: z.number().int(),
  amount: z.number().int(),
  createdBy: z.string().nullable(),
});
export type ImportSummary = z.infer<typeof ImportSummary>;
export const ImportList = z.object({ items: z.array(ImportSummary) });
export type ImportList = z.infer<typeof ImportList>;

export const ImportReadRequest = z.object({ encoding: ImportEncoding });
export const ImportMappingRequest = z.object({ mapping: ImportMapping, options: ImportOptions.optional() });
export const ImportRowUpdate = z.object({ action: z.enum(["new", "skip"]).optional(), createClient: z.boolean().optional() });
export type ImportRowUpdate = z.infer<typeof ImportRowUpdate>;
export const ImportUndoResult = z.object({ deleted: z.number().int(), kept: z.number().int(), clientsDeleted: z.number().int(), /** 取引先：前の値に戻した数（#288） */ restored: z.number().int().default(0) });
export type ImportUndoResult = z.infer<typeof ImportUndoResult>;

/** 取り込んだ請求書（Invoice.imported） */
export const InvoiceImported = z.object({ importId: z.string().uuid().nullable(), source: ImportSource.nullable(), pdf: z.boolean() });
export type InvoiceImported = z.infer<typeof InvoiceImported>;

export const IMPORT_MSG = {
  title: "データの取込",
  help: "前のシステムの CSV から取り込みます。取り込む前に全件を確認できます。取り込んだあと 14 日は取消できます。元のファイルは 7 日で自動削除します",
  newUnpaid: "請求書（未入金）取込",
  /** 取引先（#288） */
  newPartners: "取引先取込",
  toPartners: "取引先一覧へ",
  colPartnerAction: "取込",
  partnerNew: "新規",
  partnerUpdate: (name: string): string => `更新（${name}）`,
  partnerBranchOf: (row: number): string => `${row}行目の支店`,
  commitConfirmPartners: (created: number, updated: number): string => `${created} 件の取引先を登録し、${updated} 件を更新します`,
  donePartners: (created: number, updated: number, until: string): string => `${created} 件を登録し、${updated} 件を更新しました。${until}まで取込の履歴から取消できます`,
  undoConfirmPartners: "登録した取引先を削除し、更新した取引先を前の値に戻します。書類などで使っている取引先は残します",
  undonePartners: (del: number, kept: number, restored: number): string => `${del} 件を削除し、${restored} 件を前の値に戻しました${kept ? `（${kept} 件は書類などで使っているため残しました）` : ""}`,
  empty: "取込の履歴はありません",
  unchanged: "元のシステムのデータは変更・削除されません",
  stepFile: "ファイル",
  stepRead: "読み込み",
  stepMap: "列の対応",
  /** 確認の画面から列の対応へ戻るボタン */
  remap: "列設定",
  stepCheck: "確認",
  stepDone: "完了",
  source: "取込元",
  file: "ファイル",
  fileHelp: "CSV・TSV（10MB・5,000 行まで）",
  encoding: "文字コード",
  readOk: "文字が正しく表示されていれば［次へ］",
  rereadOther: (enc: string): string => `${enc}で読む`,
  rows: (n: number): string => `${n.toLocaleString("ja-JP")} 行`,
  next: "次へ",
  back: "戻る",
  noColumn: "取り込まない",
  unmapped: "取り込まない列",
  mapRequired: (label: string): string => `「${label}」の列を選んでください`,
  check: "確認",
  countsLine: (ok: number, confirm: number, error: number, skip: number): string => `取り込む ${ok}／要確認 ${confirm}／エラー ${error}／取り込まない ${skip}`,
  amount: "税込の合計",
  filterAll: "すべて",
  filterOk: "取り込む",
  filterConfirm: "要確認",
  filterError: "エラー",
  filterSkip: "取り込まない",
  skip: "取り込まない",
  unskip: "取り込む",
  createClient: (customerWord: string): string => `${customerWord}を登録`,
  notCreate: "登録しない",
  commit: "取込",
  commitConfirm: (n: number, clients: number): string => `${n} 件の請求書を発行済（取込）として登録${clients ? `し、${clients} 件の{customer}を登録` : ""}します`,
  commitBlocked: "要確認・エラーの行が残っています",
  done: (n: number, until: string): string => `${n} 件を取り込みました。${until}まで取込の履歴から取消できます`,
  toInvoices: "請求書一覧へ",
  history: "取込の履歴",
  undo: "取込取消",
  undoConfirm: (n: number): string => `取り込んだ ${n} 件の請求書を削除します。入金・領収書・送付の記録のある請求書は残します`,
  undone: (del: number, kept: number): string => `${del} 件を削除しました${kept ? `（${kept} 件は入金などの記録があるため残しました）` : ""}`,
  undoUntil: (d: string): string => `${d}まで取消できます`,
  colRow: "行",
  colState: "状態",
  imported: "取込",
  importedHelp: "前のシステムで発行した請求書を取り込んだものです。金額は前のシステムのとおりで、PDF は作成しません。元の PDF を添付できます",
  pdfAttach: "元 PDF 添付",
  pdfReplace: "元 PDF 差替",
  pdfAttached: "元の PDF を添付しました",
  pdfNone: "取り込んだ請求書の PDF はありません。元の PDF を添付すると表示できます",
  pdfOnlyPdf: "PDF ファイルを選んでください",
  locked: "取り込んだ請求書は下書きに戻せません（取込の履歴から取消できます）",
  /* 断り（API の文） */
  fileEmpty: "ファイルが空です",
  fileTooBig: "ファイルは 10MB までです",
  tooManyRows: "5,000 行までです（ファイルを分けてください）",
  tooManyCols: "1 行 200 列までです",
  noHeader: "見出しの行と1行以上のデータが要ります",
  notFound: "取込が見つかりません",
  notReady: "列の対応と確認を先に済ませてください",
  alreadyImported: "この取込は取り込み済みです",
  undoExpired: "取消できる期限を過ぎました",
  notImported: "取り込み済みの取込だけ取消できます",
} as const;

/** 指摘の文（画面の t() を通す。{customer} は会社の言葉） */
export const IMPORT_ISSUE_MSG: Record<ImportIssueCode, string> = {
  required: "空です",
  amount_bad: "金額として読めません",
  amount_mismatch: "税抜＋消費税が税込と合いません",
  amount_nonpositive: "金額が 0 以下です",
  date_bad: "日付として読めません",
  no_too_long: "50 文字までです",
  duplicate_in_file: "同じ番号がファイルの中に2行あります",
  duplicate_existing: "同じ番号の請求書があります",
  client_not_found: "{customer}が未登録です",
  due_before_issue: "お支払期限が請求日より前です",
  formula: "頭が = + - @ です（数式として扱われるおそれ）",
  amount_derived: "ほかの2つの金額から計算しました",
  code_bad: "半角英数字とハイフン・アンダーバーで20文字までです",
  type_bad: "顧客・仕入先・両方のどれかにしてください",
  phone_bad: "電話番号として読めません",
  zip_bad: "郵便番号は7桁の数字です",
  corp_bad: "法人番号は13桁の数字です",
  reg_bad: "登録番号は T＋13桁です",
  name_conflict: "同じ名前の取引先があります（コードが違います）",
  code_conflict: "このコードはほかの取引先が使っています",
  branch_required: "支店コードには支店名が要ります",
};
